import { useMutation, useQuery } from '@apollo/client';
import {
  ExistingWorkspacePreset,
  SortDirection,
  WorkspacePresetInput,
  WorkspacePresetsSource,
} from '@xometry/workspace';
import { ExistingWorkspacePreset as ErpWorkspacePreset } from 'interfaces/graphql/workspacePreset';
import { useMemo } from 'react';
import { WorkspacePresetFragment } from 'utils/graphql/fragments/__generated__/workspacePreset';
import { useWorkspacePresetsAssignDefaultPresetMutation } from 'utils/graphql/mutations/__generated__/workspacePresetsAssignDefaultPreset';
import { useWorkspacePresetsAssignGlobalDefaultPresetMutation } from 'utils/graphql/mutations/__generated__/workspacePresetsAssignGlobalDefaultPreset';
import { useWorkspacePresetsRemoveDefaultPresetMutation } from 'utils/graphql/mutations/__generated__/workspacePresetsRemoveDefaultPreset';
import { useWorkspacePresetsUpdateMutation } from 'utils/graphql/mutations/__generated__/workspacePresetsUpdate';
import {
  DefaultWorkspacePresetDocument,
  useDefaultWorkspacePresetQuery,
} from 'utils/graphql/queries/__generated__/defaultWorkspacePreset';

import {
  IWorkspacePresetsCreateResponse,
  WORKSPACE_PRESETS_CREATE,
  WORKSPACE_PRESETS_DELETE,
} from './graphql/mutations';
import { GET_WORKSPACE_PRESETS, IWorkspacePresetsResponse } from './graphql/queries';

type ErpPreset = ErpWorkspacePreset | WorkspacePresetFragment;

const toSortDirection = (direction?: string | null): SortDirection | undefined => {
  if (direction == null) return undefined;

  return direction === SortDirection.Asc ? SortDirection.Asc : SortDirection.Desc;
};

/** ERP presets carry numeric ids and `owner.name`; the library works with string ids and `fullName`. */
const toPreset = (preset: ErpPreset): ExistingWorkspacePreset => ({
  id: String(preset.id),
  name: preset.name,
  workspaceName: preset.workspaceName,
  public: preset.public,
  query: preset.query,
  columns: preset.columns,
  exportColumns: preset.exportColumns,
  createdAt: preset.createdAt,
  updatedAt: preset.updatedAt,
  sortColumn: preset.sortColumn,
  sortDirection: toSortDirection(preset.sortDirection),
  owner: preset.owner == null ? null : { id: String(preset.owner.id), fullName: preset.owner.name },
  lastUpdatedBy:
    preset.lastUpdatedBy == null ? null : { id: String(preset.lastUpdatedBy.id), fullName: preset.lastUpdatedBy.name },
});

const toAttributes = (input: WorkspacePresetInput) => ({
  name: input.name,
  public: input.public,
  workspaceName: input.workspaceName,
  query: input.query,
  columns: input.columns,
  exportColumns: input.exportColumns,
  sortColumn: input.sortColumn,
  sortDirection: input.sortDirection,
});

/** ERP's preset storage: the `workspacePresets*` GraphQL operations through Apollo. */
export const erpWorkspacePresetsSource: WorkspacePresetsSource = {
  useDefaultPresets: (workspaceName, options) => {
    const { data, loading, error } = useDefaultWorkspacePresetQuery({
      variables: { workspaceName },
      skip: options?.skip,
    });

    const personal = data?.defaultWorkspacePreset.personalDefaultPreset;
    const global = data?.defaultWorkspacePreset.globalDefaultPreset;

    return useMemo(
      () => ({
        personalDefaultPreset: personal == null ? undefined : toPreset(personal),
        globalDefaultPreset: global == null ? undefined : toPreset(global),
        loading,
        error,
      }),
      [personal, global, loading, error],
    );
  },

  usePresets: (workspaceName, options) => {
    const { data, loading, error } = useQuery<IWorkspacePresetsResponse>(GET_WORKSPACE_PRESETS, {
      variables: { workspaceName },
      skip: options?.skip,
    });

    const presets = useMemo(() => data?.workspacePresets.map(toPreset), [data]);

    return { presets, loading, error };
  },

  usePresetMutations: (workspaceName) => {
    const presetsRefetch = { refetchQueries: [{ query: GET_WORKSPACE_PRESETS, variables: { workspaceName } }] };
    const defaultRefetch = {
      refetchQueries: [{ query: DefaultWorkspacePresetDocument, variables: { workspaceName } }],
    };

    const [create] = useMutation<IWorkspacePresetsCreateResponse>(WORKSPACE_PRESETS_CREATE, {
      ...presetsRefetch,
      awaitRefetchQueries: true,
    });
    const [update] = useWorkspacePresetsUpdateMutation();
    const [destroy] = useMutation(WORKSPACE_PRESETS_DELETE, presetsRefetch);
    const [assignDefault] = useWorkspacePresetsAssignDefaultPresetMutation(defaultRefetch);
    const [removeDefault] = useWorkspacePresetsRemoveDefaultPresetMutation(defaultRefetch);
    const [assignGlobalDefault] = useWorkspacePresetsAssignGlobalDefaultPresetMutation(defaultRefetch);

    return useMemo(
      () => ({
        createPreset: async (input) => {
          const { data } = await create({ variables: { attributes: toAttributes(input) } });
          const created = data?.workspacePresetsCreate.workspacePreset;

          return created == null ? undefined : toPreset(created);
        },
        updatePreset: async (id, input) => {
          const { data } = await update({ variables: { input: { id: Number(id), attributes: toAttributes(input) } } });
          const updated = data?.workspacePresetsUpdate?.workspacePreset;

          return updated == null ? undefined : toPreset(updated);
        },
        deletePreset: async (id) => {
          await destroy({ variables: { id } });
        },
        assignPersonalDefault: async (presetId) => {
          await assignDefault({ variables: { workspaceName, presetId } });
        },
        removePersonalDefault: async () => {
          await removeDefault({ variables: { workspaceName } });
        },
        assignGlobalDefault: async (presetId) => {
          await assignGlobalDefault({ variables: { workspaceName, presetId } });
        },
      }),
      [assignDefault, assignGlobalDefault, create, destroy, removeDefault, update, workspaceName],
    );
  },
};
